/**
 * Audience Table Component
 * 
 * Displays paginated list of users in a segment with bulk selection.
 * Columns: Checkbox, Email, Name, Days Since Registration, Last Activity, 
 *          Days Inactive, Library Books, Last Contacted, Status
 */
import React, { useState, useEffect, useCallback } from "react";
import { 
  Search, ChevronDown, ChevronUp, Loader2, 
  Clock, Check, AlertCircle, Book
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { API_URL } from '@/config/api';

const AudienceTable = ({ 
  segment, 
  selectedUsers, 
  onSelectUser, 
  onSelectAll 
}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("days_since_registration");
  const [sortOrder, setSortOrder] = useState("desc");

  const fetchAudience = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        segment,
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      if (search) {
        params.append('search', search);
      }
      
      const response = await fetch(
        `${API_URL}/api/admin/marketing/audience?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch audience');
      }
      
      const data = await response.json();
      setUsers(data.users || []);
      setTotal(data.total || 0);
      setHasMore(data.has_more || false);
    } catch (err) {
      console.error('Error fetching audience:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [segment, page, pageSize, sortBy, sortOrder, search]);

  useEffect(() => {
    setPage(1);
  }, [segment, search]);

  useEffect(() => {
    fetchAudience();
  }, [fetchAudience]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchAudience();
  };

  // Filter out users in cooldown for selection purposes
  const selectableUsers = users.filter(u => !u.in_cooldown);
  const currentPageUserIds = selectableUsers.map(u => u.user_id);
  const allCurrentPageSelected = currentPageUserIds.length > 0 && 
    currentPageUserIds.every(id => selectedUsers.includes(id));
  const someCurrentPageSelected = currentPageUserIds.some(id => selectedUsers.includes(id));

  const handleSelectAllToggle = () => {
    onSelectAll(currentPageUserIds, !allCurrentPageSelected);
  };

  const handleRowCheckboxChange = (userId, inCooldown) => {
    if (inCooldown) return; // Don't allow selecting users in cooldown
    const isSelected = selectedUsers.includes(userId);
    onSelectUser(userId, !isSelected);
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return sortOrder === "asc" 
      ? <ChevronUp className="w-3 h-3 inline ml-1" />
      : <ChevronDown className="w-3 h-3 inline ml-1" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardContent className="p-0">
        {/* Search Bar */}
        <div className="p-4 border-b border-slate-800">
          <form onSubmit={handleSearchSubmit} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="audience-search-input"
              />
            </div>
            <Button 
              type="submit" 
              variant="secondary"
              className="bg-slate-800 hover:bg-slate-700"
              data-testid="audience-search-btn"
            >
              Search
            </Button>
          </form>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="py-16 text-center">
            <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-red-400">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchAudience}
              className="mt-4 border-red-700 text-red-400 hover:bg-red-900/30"
            >
              Retry
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && users.length === 0 && (
          <div className="py-16 text-center">
            <p className="text-slate-400">No users found in this segment</p>
          </div>
        )}

        {/* Table */}
        {!loading && !error && users.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="w-12 px-4">
                      <input
                        type="checkbox"
                        checked={allCurrentPageSelected}
                        onChange={handleSelectAllToggle}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-600 focus:ring-blue-500 focus:ring-offset-slate-900 cursor-pointer"
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead 
                      className="text-slate-300 cursor-pointer hover:text-white text-xs"
                      onClick={() => handleSort("email")}
                    >
                      Email <SortIcon field="email" />
                    </TableHead>
                    <TableHead 
                      className="text-slate-300 cursor-pointer hover:text-white text-xs"
                      onClick={() => handleSort("name")}
                    >
                      Name <SortIcon field="name" />
                    </TableHead>
                    <TableHead 
                      className="text-slate-300 cursor-pointer hover:text-white text-xs text-center"
                      onClick={() => handleSort("days_since_registration")}
                    >
                      Days Since Reg <SortIcon field="days_since_registration" />
                    </TableHead>
                    <TableHead className="text-slate-300 text-xs">
                      Last Activity
                    </TableHead>
                    <TableHead 
                      className="text-slate-300 cursor-pointer hover:text-white text-xs text-center"
                      onClick={() => handleSort("days_inactive")}
                    >
                      Days Inactive <SortIcon field="days_inactive" />
                    </TableHead>
                    <TableHead 
                      className="text-slate-300 cursor-pointer hover:text-white text-xs text-center"
                      onClick={() => handleSort("library_books")}
                    >
                      Library <SortIcon field="library_books" />
                    </TableHead>
                    <TableHead className="text-slate-300 text-xs">
                      Last Contacted
                    </TableHead>
                    <TableHead className="text-slate-300 text-xs text-right">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(user => {
                    const isSelected = selectedUsers.includes(user.user_id);
                    return (
                      <TableRow 
                        key={user.user_id} 
                        className={`border-slate-800 hover:bg-slate-800/50 ${isSelected ? 'bg-blue-900/20' : ''} ${user.in_cooldown ? 'opacity-60' : ''}`}
                        data-testid={`user-row-${user.user_id}`}
                      >
                        <TableCell className="px-4">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleRowCheckboxChange(user.user_id, user.in_cooldown)}
                            disabled={user.in_cooldown}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-600 focus:ring-blue-500 focus:ring-offset-slate-900 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                            data-testid={`user-checkbox-${user.user_id}`}
                          />
                        </TableCell>
                        <TableCell className="text-white font-medium text-sm">
                          {user.email}
                        </TableCell>
                        <TableCell className="text-slate-300 text-sm">
                          {user.name || '-'}
                        </TableCell>
                        <TableCell className="text-slate-400 text-sm text-center">
                          <span className="font-medium text-white">{user.days_since_registration}</span>
                          <span className="text-xs text-slate-500 ml-1">days</span>
                        </TableCell>
                        <TableCell className="text-slate-400 text-sm">
                          {user.last_activity_at ? formatDate(user.last_activity_at) : 'Never'}
                        </TableCell>
                        <TableCell className="text-slate-400 text-sm text-center">
                          {user.days_inactive !== null && user.days_inactive !== undefined ? (
                            <>
                              <span className={`font-medium ${user.days_inactive > 30 ? 'text-amber-400' : 'text-white'}`}>
                                {user.days_inactive}
                              </span>
                              <span className="text-xs text-slate-500 ml-1">days</span>
                            </>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Book className="w-3 h-3 text-slate-500" />
                            <span className="text-white font-medium">{user.library_books}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-400 text-sm">
                          {user.last_contacted_at ? formatDate(user.last_contacted_at) : 'Never'}
                        </TableCell>
                        <TableCell className="text-right">
                          {user.in_cooldown ? (
                            <Badge variant="outline" className="border-amber-500/50 text-amber-400 bg-amber-900/20 text-xs">
                              <Clock className="w-3 h-3 mr-1" />
                              7d Cooldown
                            </Badge>
                          ) : user.last_contacted_at ? (
                            <Badge variant="outline" className="border-slate-600 text-slate-400 text-xs">
                              <Check className="w-3 h-3 mr-1" />
                              Contacted
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-emerald-500/50 text-emerald-400 bg-emerald-900/20 text-xs">
                              Available
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800">
              <span className="text-sm text-slate-400">
                Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 1}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="prev-page-btn"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={!hasMore}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="next-page-btn"
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AudienceTable;
